import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import MarkdownBody from "./MarkdownBody";
import { API_BASE_URL } from "@/lib/apiBase";
import {
  entitlementMessageFor,
  getEntitlementCode,
  toastEntitlementOrError,
} from "@/lib/entitlementToast";
import { messageForCatchupGenerateError } from "@/lib/catchupGenerateError";
import {
  permalinkForPost,
  withCatchupReturn,
} from "@/lib/permalinks";

/** Canonical thread permalink with catch-up return query. */
function buildCatchupThreadHref(bullet, orgId, returnPath) {
  const postId = bullet?.post_id ?? bullet?.postId;
  const commentId = bullet?.comment_id ?? bullet?.commentId;
  if (postId != null && orgId) {
    let href = permalinkForPost({ orgId, postId });
    if (commentId != null) {
      href = `${href}?comment=${encodeURIComponent(commentId)}`;
    }
    return withCatchupReturn(href, returnPath);
  }
  const href = bullet?.href;
  if (!href) return null;
  try {
    const url = new URL(href, window.location.origin);
    const legacyPost =
      url.searchParams.get("post") || url.searchParams.get("card");
    if (legacyPost && orgId) {
      let permalink = permalinkForPost({ orgId, postId: legacyPost });
      if (commentId != null) {
        permalink = `${permalink}?comment=${encodeURIComponent(commentId)}`;
      }
      return withCatchupReturn(permalink, returnPath);
    }
  } catch {
    /* fall through */
  }
  return withCatchupReturn(href, returnPath);
}

function formatShortDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  } catch {
    return null;
  }
}

/** Briefing with clickable cited bullets. Uncited bullets are dropped server-side. */
function CatchUpBriefing({
  briefing,
  returnPath,
  orgId,
  showThreadLinks = true,
  /** When false, hide team / org-wide place labels (e.g. single-team board summary). */
  showPlaceLabel = true,
}) {
  if (!briefing || typeof briefing !== "object") return null;

  const scrubText = (text) => {
    if (!text || typeof text !== "string") return text;
    if (showPlaceLabel) return text;
    // Drop model-echoed "[Team Name]" prefixes on single-team briefings.
    return text.replace(/^\[[^\]]+\]\s*/, "").trim();
  };

  return (
    <div className="space-y-3 text-sm">
      {briefing.tldr ? (
        <p className="text-foreground leading-relaxed">
          {scrubText(briefing.tldr)}
        </p>
      ) : null}
      {(briefing.sections || []).map((section) => (
        <div key={section.id || section.title} className="space-y-1.5">
          <h4 className="text-sm font-semibold text-foreground">
            {section.title || section.id}
          </h4>
          <ul className="list-disc pl-5 space-y-1.5">
            {(section.bullets || []).map((bullet, idx) => {
              const placeLabel = showPlaceLabel
                ? bullet.team_name ||
                  (bullet.team_id == null && bullet.post_id != null
                    ? "Org-wide"
                    : null)
                : null;
              const meta = [
                placeLabel,
                bullet.author_name,
                bullet.kind === "card" ? "card" : null,
              ]
                .filter(Boolean)
                .join(" · ");
              const commentId = bullet.comment_id ?? bullet.commentId;
              const to =
                showThreadLinks || commentId != null
                  ? buildCatchupThreadHref(bullet, orgId, returnPath)
                  : null;
              const text = scrubText(bullet.text);
              if (!text) return null;
              return (
                <li key={`${section.id}-${idx}`} className="leading-snug">
                  {to ? (
                    <Link
                      to={to}
                      className="text-primary underline-offset-2 hover:underline"
                    >
                      {text}
                    </Link>
                  ) : (
                    <span>{text}</span>
                  )}
                  {meta ? (
                    <span className="text-muted-foreground"> — {meta}</span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
      {briefing.omitted_unlinked ? (
        <p className="text-xs text-muted-foreground">
          Some items could not be linked and were omitted.
        </p>
      ) : null}
    </div>
  );
}

function briefingTldr(summary) {
  const json = summary?.summary_json;
  if (json && typeof json === "object" && json.tldr) {
    return String(json.tldr).trim();
  }
  return (summary?.summary_md || "").trim();
}

function hasUsableSummary(summary) {
  if (!summary) return false;
  const json = summary.summary_json;
  if (
    json &&
    typeof json === "object" &&
    (json.tldr || (json.sections || []).length > 0)
  ) {
    return true;
  }
  return Boolean((summary.summary_md || "").trim());
}

/** True when the user has marked caught up since this summary was generated. */
function shouldStartCollapsed(summary, lastSeenAt) {
  if (!summary || !lastSeenAt) return false;
  const generated = new Date(summary.generated_at || summary.generatedAt).getTime();
  const seen = new Date(lastSeenAt).getTime();
  if (Number.isNaN(generated) || Number.isNaN(seen)) return false;
  return seen >= generated;
}

function formatFeedSummaryFrom(generatedAt) {
  if (!generatedAt) return null;
  const then = new Date(generatedAt).getTime();
  if (Number.isNaN(then)) return null;
  const diffMs = Math.max(0, Date.now() - then);
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 60) {
    const n = Math.max(1, minutes);
    return `${n} ${n === 1 ? "minute" : "minutes"} ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  }
  const days = Math.floor(hours / 24);
  return `${days} ${days === 1 ? "day" : "days"} ago`;
}

function formatAwaySince(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const diffMs = Math.max(0, Date.now() - then);
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 60) {
    const n = Math.max(1, minutes);
    return `${n} ${n === 1 ? "minute" : "minutes"} ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  }
  const days = Math.floor(hours / 24);
  if (days < 7) {
    return `${days} ${days === 1 ? "day" : "days"} ago`;
  }
  return formatShortDate(iso);
}

function formatCatchupEta(activityCount) {
  const n = Number(activityCount) || 0;
  if (n <= 0) return null;
  const minutes = Math.min(10, Math.max(1, Math.ceil((n * 15) / 60)));
  return `~${minutes} min`;
}

const NOTHING_NEW_TOAST =
  "Nothing new since you were last here — showing your last summary.";

/**
 * Per-user AI catch-up for workplace feeds (org / team). Hidden on social.
 * Loads cached briefing only; generate/update is always explicit (button).
 */
const CatchUpCard = ({
  orgId,
  scope,
  teamId,
  accessToken,
  canManageSettings = false,
  onAwarenessChange,
}) => {
  const location = useLocation();
  const returnPath = `${location.pathname}${location.search || ""}`;
  const [summary, setSummary] = useState(null);
  const [canGenerate, setCanGenerate] = useState(true);
  const [cooldownUntil, setCooldownUntil] = useState(null);
  const [lastSeenAt, setLastSeenAt] = useState(null);
  const [defaultWindowStart, setDefaultWindowStart] = useState(null);
  const [activityCount, setActivityCount] = useState(0);
  const [activityPosts, setActivityPosts] = useState(0);
  const [activityCards, setActivityCards] = useState(0);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const generatingRef = useRef(false);
  const onAwarenessChangeRef = useRef(onAwarenessChange);
  onAwarenessChangeRef.current = onAwarenessChange;

  const authHeaders = useCallback(
    () => ({ Authorization: `Bearer ${accessToken}` }),
    [accessToken]
  );

  const applyActivityCounts = useCallback((data) => {
    const count =
      Number(data?.activity_count ?? data?.item_count ?? 0) || 0;
    setActivityCount(count);
    if (data?.activity_posts != null || data?.activity_cards != null) {
      setActivityPosts(Number(data.activity_posts) || 0);
      setActivityCards(Number(data.activity_cards) || 0);
    } else if (data?.item_count != null && data?.activity_count == null) {
      setActivityPosts(Number(data.item_count) || 0);
      setActivityCards(0);
    }
    return count;
  }, []);

  const applyMeta = useCallback(
    (data, { expand = false } = {}) => {
      const nextSummary = data?.summary || null;
      setSummary(nextSummary);
      setLastSeenAt(data?.last_seen_at || null);
      setDefaultWindowStart(data?.default_window_start || null);
      const count = applyActivityCounts(data);

      if (!nextSummary) {
        setCanGenerate(true);
        setCooldownUntil(null);
        setCollapsed(false);
        return {
          summary: null,
          collapsed: false,
          canGenerate: true,
          activityCount: count,
        };
      }
      const nextCanGenerate = data?.can_generate !== false;
      setCanGenerate(nextCanGenerate);
      setCooldownUntil(data?.cooldown_until || null);
      let nextCollapsed;
      if (expand) {
        nextCollapsed = false;
        setCollapsed(false);
      } else {
        nextCollapsed = shouldStartCollapsed(nextSummary, data?.last_seen_at);
        setCollapsed(nextCollapsed);
      }
      return {
        summary: nextSummary,
        collapsed: nextCollapsed,
        canGenerate: nextCanGenerate,
        activityCount: count,
      };
    },
    [applyActivityCounts]
  );

  const showToast = useCallback((message) => {
    setToast(message);
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const id = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(id);
  }, [toast]);

  // Notify parent when behind/caught-up settles (org Dashboard feed bias).
  // Peeking a prior summary after Mark as caught up is not "behind".
  useEffect(() => {
    if (!onAwarenessChangeRef.current || loading || generating) return;
    const usable = hasUsableSummary(summary);
    const alreadyCaughtUp = shouldStartCollapsed(summary, lastSeenAt);
    const behind = !collapsed && usable && !alreadyCaughtUp;
    const windowStart = lastSeenAt || defaultWindowStart || null;
    onAwarenessChangeRef.current({ behind, windowStart });
  }, [
    loading,
    generating,
    collapsed,
    summary,
    lastSeenAt,
    defaultWindowStart,
  ]);

  const runGenerate = useCallback(
    async ({ force, expand }) => {
      if (!accessToken || !orgId || generatingRef.current) return null;
      generatingRef.current = true;
      setGenerating(true);
      setError(null);
      try {
        const body = { scope, force: force === true };
        if (scope === "team" && teamId != null) {
          body.team_id = Number(teamId);
        }
        const { data } = await axios.post(
          `${API_BASE_URL}/catchup/generate`,
          body,
          {
            params: { org_id: orgId },
            headers: authHeaders(),
            withCredentials: true,
          }
        );
        const meta = applyMeta(data, { expand: expand !== false });
        if (data?.reason === "nothing_new") {
          showToast(NOTHING_NEW_TOAST);
        }
        return { data, meta };
      } catch (err) {
        if (getEntitlementCode(err)) {
          const msg = entitlementMessageFor(err, { canManageSettings });
          setError(msg);
          toastEntitlementOrError(err, "Failed to generate catch-up.", {
            canManageSettings,
            orgId,
          });
        } else {
          setError(
            messageForCatchupGenerateError(err, {
              fallback: "Failed to generate catch-up.",
            })
          );
        }
        return null;
      } finally {
        generatingRef.current = false;
        setGenerating(false);
      }
    },
    [
      accessToken,
      orgId,
      scope,
      teamId,
      authHeaders,
      canManageSettings,
      showToast,
      applyMeta,
    ]
  );

  const fetchCached = useCallback(async () => {
    if (!accessToken || !orgId || !scope) return;
    setLoading(true);
    setError(null);
    try {
      const params = { org_id: orgId, scope };
      if (scope === "team" && teamId != null) {
        params.team_id = teamId;
      }
      const { data } = await axios.get(`${API_BASE_URL}/catchup`, {
        params,
        headers: authHeaders(),
        withCredentials: true,
      });
      applyMeta(data);
    } catch (err) {
      console.error("Failed to load catch-up:", err);
      setError(
        err.response?.data?.message || "Could not load catch-up summary."
      );
    } finally {
      setLoading(false);
    }
  }, [accessToken, orgId, scope, teamId, authHeaders, applyMeta]);

  useEffect(() => {
    fetchCached();
  }, [fetchCached]);

  const handleGenerate = () => {
    runGenerate({ force: true, expand: true });
  };

  const handleMarkCaughtUp = async () => {
    if (!accessToken || !orgId) return;
    setError(null);
    try {
      const body = { scope };
      if (scope === "team" && teamId != null) {
        body.team_id = Number(teamId);
      }
      await axios.post(`${API_BASE_URL}/catchup/seen`, body, {
        params: { org_id: orgId },
        headers: authHeaders(),
        withCredentials: true,
      });
      setCollapsed(true);
      setLastSeenAt(new Date().toISOString());
      setActivityCount(0);
      setActivityPosts(0);
      setActivityCards(0);
      try {
        const params = { org_id: orgId, scope };
        if (scope === "team" && teamId != null) {
          params.team_id = teamId;
        }
        const { data } = await axios.get(`${API_BASE_URL}/catchup`, {
          params,
          headers: authHeaders(),
          withCredentials: true,
        });
        applyMeta(data);
      } catch {
        /* local caught-up chrome already applied */
      }
    } catch (err) {
      setError(
        err.response?.data?.message || "Could not mark catch-up as seen."
      );
    }
  };

  if (!orgId || !accessToken || orgId === "social") {
    return null;
  }

  const summaryMd = summary?.summary_md;
  const summaryJson = summary?.summary_json;
  const hasStructured =
    summaryJson &&
    typeof summaryJson === "object" &&
    (summaryJson.tldr || (summaryJson.sections || []).length > 0);
  const hasSummary = Boolean(hasStructured || summaryMd);
  const generateDisabled =
    generating || loading || (hasSummary && !canGenerate);
  const tldr = briefingTldr(summary);
  const compactTldr =
    tldr.length > 120 ? `${tldr.slice(0, 117).trimEnd()}…` : tldr;
  const feedSummaryAge = formatFeedSummaryFrom(summary?.generated_at);
  const awaySince = formatAwaySince(lastSeenAt || defaultWindowStart);
  const eta = !collapsed && activityCount > 0
    ? formatCatchupEta(activityCount)
    : null;

  const coverStart =
    formatShortDate(summary?.window_start || defaultWindowStart);
  const coverEnd = formatShortDate(summary?.window_end || new Date().toISOString());
  const coveringLine =
    hasSummary && coverStart
      ? `Based on ${activityPosts} post${
          activityPosts === 1 ? "" : "s"
        } and ${activityCards} card${
          activityCards === 1 ? "" : "s"
        } you can see · Covering ${coverStart}–${coverEnd || "now"}`
      : null;
  const peekingPriorSummary =
    hasSummary && shouldStartCollapsed(summary, lastSeenAt);

  let cooldownHint = null;
  if (hasSummary && !canGenerate && cooldownUntil) {
    try {
      cooldownHint = `Next update available ${new Date(
        cooldownUntil
      ).toLocaleString()}`;
    } catch {
      cooldownHint = "Next update available after cooldown.";
    }
  }

  const toastBanner = toast ? (
    <p
      className="w-full text-sm rounded-md px-0 py-1 text-foreground"
      role="status"
    >
      {toast}
    </p>
  ) : null;

  const awaySubtitleParts = [];
  if (awaySince) awaySubtitleParts.push(`Since ${awaySince}`);
  else awaySubtitleParts.push("Since you were last here");
  if (eta) awaySubtitleParts.push(eta);
  if (feedSummaryAge) awaySubtitleParts.push(`Feed summary from ${feedSummaryAge}`);

  if (collapsed && hasSummary) {
    const staleNew = activityCount > 0;
    return (
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/50 pb-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground">
            {staleNew
              ? `${activityCount} new since you marked caught up`
              : "Caught up"}
          </p>
          {compactTldr ? (
            <p className="text-xs text-muted-foreground truncate mt-0.5">
              {compactTldr}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-1 shrink-0">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-8 text-muted-foreground"
            onClick={() => setCollapsed(false)}
          >
            Show summary
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-8 text-muted-foreground"
            disabled={generateDisabled}
            onClick={handleGenerate}
          >
            {generating ? "Updating…" : "Update"}
          </Button>
        </div>
        {toastBanner}
        {error && (
          <p className="w-full text-sm text-destructive" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3 border-b border-border/50 pb-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-foreground">
            {peekingPriorSummary
              ? "Last summary"
              : hasSummary
                ? "You're behind"
                : "Catch up"}
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {awaySubtitleParts.join(" · ")}
          </p>
          {coveringLine ? (
            <p className="text-xs text-muted-foreground mt-0.5">{coveringLine}</p>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-1">
          {peekingPriorSummary ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-8 text-muted-foreground"
              disabled={generating}
              onClick={() => setCollapsed(true)}
            >
              Hide summary
            </Button>
          ) : null}
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-8 text-muted-foreground"
            disabled={generateDisabled || (!hasSummary && activityCount === 0)}
            onClick={handleGenerate}
          >
            {generating
              ? hasSummary
                ? "Updating…"
                : "Generating…"
              : hasSummary
                ? "Update"
                : "Generate"}
          </Button>
          {hasSummary && !peekingPriorSummary ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8"
              disabled={generating}
              onClick={handleMarkCaughtUp}
            >
              Mark as caught up
            </Button>
          ) : null}
        </div>
      </div>

      {toastBanner}

      {cooldownHint && (
        <p className="text-xs text-muted-foreground">{cooldownHint}</p>
      )}

      {(loading || generating) && !hasSummary && (
        <p className="text-sm text-muted-foreground">
          {generating ? "Generating catch-up…" : "Loading catch-up…"}
        </p>
      )}

      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}

      {!loading && !generating && !hasSummary && !error && activityCount === 0 && (
        <p className="text-sm text-muted-foreground">
          Nothing in this window yet. Catch-up appears when there is activity.
        </p>
      )}

      {!loading && !generating && !hasSummary && !error && activityCount > 0 && (
        <p className="text-sm text-muted-foreground">
          {awaySince
            ? `Away since ${awaySince} · ${activityCount} update${
                activityCount === 1 ? "" : "s"
              } ready to summarize.`
            : `${activityCount} update${
                activityCount === 1 ? "" : "s"
              } ready to summarize.`}
        </p>
      )}

      {hasStructured ? (
        <CatchUpBriefing
          briefing={summaryJson}
          returnPath={returnPath}
          orgId={orgId}
        />
      ) : summaryMd ? (
        <MarkdownBody content={summaryMd} compact className="text-sm" />
      ) : null}
    </div>
  );
};

export default CatchUpCard;

/** Shared briefing renderer for permalink entity summarize. */
export { CatchUpBriefing };
